import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Session } from '@supabase/supabase-js';
import { consumeInitialAuthFlowType, supabase } from './supabase';
import { formatError } from './errors';
import type { OperatorProfile, PermissionKey } from '../types';

interface AuthState {
  session: Session | null;
  operator: OperatorProfile | null;
  loading: boolean;
  error: string | null;
  // UI-side permission check. Cosmetic only — every gated page is also
  // enforced in the database (operations.fn_assert_permission inside the
  // SECURITY DEFINER RPCs), so a bypass here still gets a 42501.
  can: (permission: PermissionKey) => boolean;
  // True when the active session came from an invite or password-recovery
  // email — staff still needs to set a password before normal use.
  passwordSetupRequired: boolean;
  signOut: () => Promise<void>;
  refreshOperator: (userId: string | null) => Promise<void>;
  markPasswordSet: () => void;
}

const AuthCtx = createContext<AuthState | null>(null);

const QUERY_TIMEOUT_MS = 8000;
const SESSION_BOOTSTRAP_TIMEOUT_MS = 3000;
const SESSION_STORAGE_KEY = 'neoseum-admin-auth';
const PASSWORD_SETUP_FLAG_KEY = 'neoseum-admin-password-setup-required';

function readPasswordSetupFlag(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(PASSWORD_SETUP_FLAG_KEY) === '1';
  } catch {
    return false;
  }
}

function writePasswordSetupFlag(value: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    if (value) {
      window.sessionStorage.setItem(PASSWORD_SETUP_FLAG_KEY, '1');
    } else {
      window.sessionStorage.removeItem(PASSWORD_SETUP_FLAG_KEY);
    }
  } catch {
    /* ignore */
  }
}

interface StoredSession {
  access_token?: string;
  refresh_token?: string;
  expires_at?: number;
  user?: { id?: string; email?: string };
}

// Read the persisted Supabase session straight from localStorage. supabase-js
// uses this same key (configured in lib/supabase.ts). Reading directly lets us
// recover the user id without waiting on supabase.auth.getSession(), which has
// been observed to hang behind a stuck token refresh.
function readStoredSession(): StoredSession | null {
  try {
    const raw = window.localStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed as StoredSession;
    return null;
  } catch {
    return null;
  }
}

async function getSessionWithTimeout(): Promise<Session | null> {
  const liveSession = supabase.auth.getSession().then(({ data }) => data?.session ?? null);
  const timeout = new Promise<'timeout'>((resolve) =>
    setTimeout(() => resolve('timeout'), SESSION_BOOTSTRAP_TIMEOUT_MS),
  );
  const winner = await Promise.race([liveSession, timeout]);
  if (winner === 'timeout') {
    console.warn('[admin] supabase.auth.getSession() slow — falling back to localStorage');
    const stored = readStoredSession();
    if (!stored?.access_token || !stored.user?.id) return null;
    // Cast a stored snapshot into Session shape — only the fields we read
    // (access_token, user.id, user.email) are populated.
    return {
      access_token: stored.access_token,
      refresh_token: stored.refresh_token ?? '',
      expires_at: stored.expires_at,
      expires_in: 0,
      token_type: 'bearer',
      user: {
        id: stored.user.id,
        email: stored.user.email,
        app_metadata: {},
        user_metadata: {},
        aud: 'authenticated',
        created_at: '',
      },
    } as unknown as Session;
  }
  return winner;
}

type LoadOperatorResult =
  | { ok: true; operator: OperatorProfile | null }
  | { ok: false; error: string };

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

async function loadOperator(userId: string): Promise<LoadOperatorResult> {
  // Raw fetch against PostgREST. Going through supabase-js here causes hangs
  // when supabase-js is mid-token-refresh: queries get queued behind the
  // pending auth state and don't fire until the refresh resolves. We pull
  // the access_token straight out of localStorage to avoid the same hang
  // inside supabase.auth.getSession().
  const accessToken = readStoredSession()?.access_token ?? ANON_KEY;

  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), QUERY_TIMEOUT_MS);

  try {
    // operations.fn_current_operator() rather than a table select: it resolves
    // auth.uid() -> operator AND returns effective_permissions (role grants
    // UNION individual grants, everything for admins). The table can't give us
    // that — role_permissions isn't readable from the client. userId is only
    // used for logging; the function keys off the JWT.
    const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/fn_current_operator`, {
      method: 'POST',
      headers: {
        apikey: ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
        'Content-Profile': 'operations',
        'Accept-Profile': 'operations',
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: '{}',
      signal: ctrl.signal,
    });

    if (!res.ok) {
      let body: unknown = null;
      try { body = await res.json(); } catch { /* ignore */ }
      const msg = `HTTP ${res.status} ${formatError(body) || res.statusText}`;
      console.error('[admin] operator lookup failed:', msg, body);
      return { ok: false, error: msg };
    }

    const rows = (await res.json()) as OperatorProfile[];
    const row = rows[0] ?? null;
    if (!row) {
      // fn_current_operator filters on is_active, so "no row" covers both
      // "not linked to an operator" and "deactivated".
      console.warn(`[admin] no active operator row for user_id=${userId}`);
      return { ok: true, operator: null };
    }
    return {
      ok: true,
      operator: { ...row, effective_permissions: row.effective_permissions ?? [] },
    };
  } catch (e) {
    const msg =
      (e as { name?: string })?.name === 'AbortError'
        ? `operator lookup timed out after ${QUERY_TIMEOUT_MS}ms`
        : formatError(e);
    console.error(`[admin] operator lookup failed: ${msg}`);
    return { ok: false, error: msg };
  } finally {
    clearTimeout(timer);
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [operator, setOperator] = useState<OperatorProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [passwordSetupRequired, setPasswordSetupRequired] = useState<boolean>(
    () => readPasswordSetupFlag(),
  );
  const inFlightRef = useRef(false);

  const markPasswordSetup = useCallback(() => {
    writePasswordSetupFlag(true);
    setPasswordSetupRequired(true);
  }, []);

  const markPasswordSet = useCallback(() => {
    writePasswordSetupFlag(false);
    setPasswordSetupRequired(false);
  }, []);

  const refreshOperator = useCallback(async (userId: string | null) => {
    if (!userId) {
      setOperator(null);
      setError(null);
      return;
    }
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const result = await loadOperator(userId);
      if (!result.ok) {
        // Transient lookup failure (timeout, network blip). Don't wipe the
        // operator state if we already have one — the user is working and we
        // shouldn't kick them back to the error screen for a flaky retry.
        setOperator((prev) => {
          if (prev) return prev;
          setError(result.error);
          return prev;
        });
        return;
      }
      setOperator(result.operator);
      setError(
        result.operator
          ? null
          : 'No active operator profile linked to this account. Ask an admin to link your auth user to operations.operators.',
      );
    } catch (e) {
      console.error('[admin] refreshOperator threw', e);
      setError(formatError(e));
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setOperator(null);
    setError(null);
    writePasswordSetupFlag(false);
    setPasswordSetupRequired(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await getSessionWithTimeout();
        if (cancelled) return;
        setSession(s);
        if (s?.user?.id) {
          await refreshOperator(s.user.id);
        }
      } catch (e) {
        console.error('[admin] auth bootstrap threw', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    const { data: sub } = supabase.auth.onAuthStateChange(async (event, s) => {
      if (cancelled) return;
      setSession(s);
      // Only re-fetch operator on sign-in / sign-out events. TOKEN_REFRESHED
      // and USER_UPDATED fire periodically and shouldn't trigger a new
      // operator lookup — that's noise that races with in-flight requests.
      if (event === 'SIGNED_OUT') {
        setOperator(null);
        setError(null);
        writePasswordSetupFlag(false);
        setPasswordSetupRequired(false);
        return;
      }
      // Password-reset emails fire PASSWORD_RECOVERY. Invite emails fire a
      // plain SIGNED_IN in supabase-js v2 — they never get a dedicated event,
      // so we sniff the original URL hash (captured before supabase-js cleaned
      // it) to detect them. Both paths gate the app on /set-password.
      if (event === 'PASSWORD_RECOVERY') {
        markPasswordSetup();
      }
      if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
        // One-shot: only the very first auth event after page load can claim
        // the invite/recovery flag. Later sign-ins on the same page session
        // get null, so a normal log-out → log-in cycle doesn't force the user
        // through /set-password again.
        const flowType = consumeInitialAuthFlowType();
        if (flowType === 'invite' || flowType === 'recovery') {
          markPasswordSetup();
        }
        if (s?.user?.id) {
          await refreshOperator(s.user.id);
        }
      }
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, [refreshOperator]);

  // Admin short-circuits everything (matches operations.fn_has_permission).
  const can = useCallback(
    (permission: PermissionKey): boolean => {
      if (!operator) return false;
      if (operator.role === 'admin') return true;
      return (operator.effective_permissions ?? []).includes(permission);
    },
    [operator],
  );

  const value = useMemo<AuthState>(
    () => ({
      session,
      operator,
      loading,
      error,
      can,
      passwordSetupRequired,
      signOut,
      refreshOperator,
      markPasswordSet,
    }),
    [
      session,
      operator,
      loading,
      error,
      can,
      passwordSetupRequired,
      signOut,
      refreshOperator,
      markPasswordSet,
    ],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
